import { Booking } from '@bookings/interfaces/booking';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BookingForm from '../booking-form/booking-form.vue';
import { BookingModule } from '@bookings/store/booking.store';
import { Module } from 'vuex';
import { BookingFormState } from '@bookings/interfaces/booking-form-store';
import { RootState } from '@core/interfaces/root-state';
import { cloneDeep } from 'lodash';
import { PosSession } from '@pos/interfaces/pos-session';
import ModulesHelper from '@core/services/modules.helper';
import AddPaymentModal from '@accounting/components/modals/add-payment-modal/add-payment-modal.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider.vue';

@Component({
  name: 'bpc-drawer-booking-form',
  components: {
    'bpc-drawer-modal': DrawerModal,
    'bpc-booking-form': BookingForm,
    'bc-add-payment-modal': AddPaymentModal,
    'bpc-std-form': StdForm,
  },
})
export default class DrawerBookingForm extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    bookingForm: InstanceType<typeof BookingForm> & {
      clickOnSaveButton: () => Promise<string>;
      clickOnSearchButton: () => Promise<string>;
      clickOnReplaceButton: () => Promise<string>;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: AnyEntity;
      formProvider: EntityFormProvider;
    };
  };
  s;
  /* Lifecycle hooks */
  // created () { }
  @Prop(Boolean) readonly showDrawer: boolean;
  @Prop([Number, String]) readonly entityId: number | string;
  @Prop(Object) readonly entity: Booking;
  @Prop(Number) readonly activityGroupId: number;
  @Prop(String) readonly title: string;
  @Prop({ type: String, default: 'default' }) readonly createFormConfigName: string;
  @Prop({ type: String, default: 'default-edit' }) readonly editFormConfigName: string;
  @Prop({ type: Number, default: 0 }) readonly deep: number;
  @Prop({ type: String, default: 'api' }) readonly mode: string;
  @Prop({ type: Boolean, default: false }) readonly withDraft: boolean;
  @Prop({ type: String }) readonly bookingStoreName: string;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop([String, Object, Array]) readonly extraData: string | AnyEntity;

  public moduleReady: boolean = false;
  public deleting: boolean = false;
  public cancelling: boolean = false;
  public refusing: boolean = false;
  public confirming: boolean = false;

  // Add payment modal
  public showPaymentModal: boolean = false;

  public showCancellingSpinner: boolean = false;

  async beforeMount() {
    if (this.storeName !== 'booking' && !this.$store.hasModule(this.storeName)) {
      console.log('registering module', this.storeName);
      const mutations = cloneDeep(BookingModule.mutations);
      const getters = cloneDeep(BookingModule.getters);
      const state = cloneDeep(BookingModule.state);
      const actions = cloneDeep(BookingModule.actions);

      const mod = {
        namespaced: true,
        state,
        actions,
        mutations,
        getters,
      } as Module<BookingFormState, RootState>;

      await this.$store.registerModule(this.storeName, mod);

      console.log('module registered', this.storeName);
      console.log('this.$store', this.$store);
    }
    this.moduleReady = true;

    this.$store.commit(`${this.storeName}/setMode`, this.mode);

    if (this.showDrawer) {
      this.showDrawerModal();
    }
  }

  get storeName(): string {
    if (this.bookingStoreName) {
      return this.bookingStoreName;
    } else {
      const initial = this.entityId ? `booking_${this.entityId}` : 'booking';
      return `${initial}_${this.deep}`;
    }
  }

  get isSomeTopLayerActionsDisplayed(): boolean {
    return (
      (this.showCancelButton && this.bookingCanBeCancelled()) ||
      (this.showConfirmButton && this.bookingCanBeConfirmed()) ||
      (this.showRefuseButton && this.bookingCanBeRefused())
    );
  }

  // TODO implement recursive form
  // @Prop({ type: Number, default: 0 }) readonly deep: number;
  // get storeName(): string {
  //   const initial = this.entityId ? `booking_${this.entityId}` : 'bookingEvent';
  //   return `${initial}_${this.deep}`;
  // }

  public get showCancelButton(): boolean {
    return this.$store.getters[`${this.storeName}/showCancelButton`];
  }

  public get showConfirmButton(): boolean {
    return this.$store.getters[`${this.storeName}/showConfirmButton`];
  }

  public get showRefuseButton(): boolean {
    return this.$store.getters[`${this.storeName}/showRefuseButton`];
  }

  public get showDeleteButton(): boolean {
    return this.$store.getters[`${this.storeName}/showDeleteButton`];
  }

  public get showCloseButton(): boolean {
    return this.$store.getters[`${this.storeName}/showCloseButton`];
  }

  public get showSearchButton(): boolean {
    return this.$store.getters[`${this.storeName}/showSearchButton`];
    // return !this.showSaveButton;
  }

  public get showSaveButton(): boolean {
    return this.$store.getters[`${this.storeName}/showSaveButton`];
  }

  public get showReplaceButton(): boolean {
    return this.$store.getters[`${this.storeName}/showReplaceButton`];
  }

  public get showSearchingSpinner(): boolean {
    return this.$store.getters[`${this.storeName}/isSearching`];
  }

  public get showSavingSpinner(): boolean {
    return this.$store.getters[`${this.storeName}/isSaving`];
  }

  public get numberOfButtonCols(): number {
    return this.showSearchButton && this.showSaveButton ? 4 : 6;
  }

  public get showLargeDrawer(): boolean {
    return this.$store.getters[`${this.storeName}/showLargeDrawer`];
  }

  public get searchButtonDisabled(): boolean {
    return this.$store.getters[`${this.storeName}/searchButtonDisabled`];
  }

  public get saveButtonDisabled(): boolean {
    return this.$store.getters[`${this.storeName}/saveButtonDisabled`];
  }

  public get realFormConfigName(): string {
    return this.entityId ? this.editFormConfigName : this.createFormConfigName;
  }

  public get bookingStatus(): string {
    return this.$store.getters[`${this.storeName}/bookingStatus`];
  }

  public get cancelModalTitle() {
    return this.$t(`global.modals.cancel.title`, {
      entityName: this.$t('global.models.booking.name'),
      entityPluralName: this.$t('global.models.booking.pluralName'),
      thisEntityName: this.$t('global.models.booking.thisName'),
      thisEntityPluralName: this.$t('global.models.booking.thisPluralName'),
    });
  }

  public get cancelModalBody() {
    return this.$t(`global.modals.cancel.body`, {
      entityName: this.$t('global.models.booking.name'),
      entityPluralName: this.$t('global.models.booking.pluralName'),
      thisEntityName: this.$t('global.models.booking.thisName'),
      thisEntityPluralName: this.$t('global.models.booking.thisPluralName'),
    });
  }

  public get deleteModalTitle() {
    return this.$t(`global.modals.soft-delete.title`, {
      entityName: this.$t('global.models.booking.name'),
      entityPluralName: this.$t('global.models.booking.pluralName'),
      thisEntityName: this.$t('global.models.booking.thisName'),
      thisEntityPluralName: this.$t('global.models.booking.thisPluralName'),
    });
  }

  public get deleteModalBody() {
    return this.$t(`global.modals.soft-delete.body`, {
      entityName: this.$t('global.models.booking.name'),
      entityPluralName: this.$t('global.models.booking.pluralName'),
      thisEntityName: this.$t('global.models.booking.thisName'),
      thisEntityPluralName: this.$t('global.models.booking.thisPluralName'),
    });
  }

  public get confirmModalTitle() {
    return this.$t(`bookings.modals.confirm.title`);
  }

  public get confirmModalBody() {
    return this.$t(`bookings.modals.confirm.body`);
  }

  public get refuseModalTitle() {
    return this.$t(`bookings.modals.refuse.title`);
  }

  public get refuseModalBody() {
    return this.$t(`bookings.modals.refuse.body`);
  }

  public i: number = 1;
  showDrawerSection: boolean = false;
  showForm: boolean = false;

  mounted() {
    if (this.activityGroupId) {
      setTimeout(() => {
        this.$store.commit(`booking/setModelProp`, {
          prop: 'activity_group_id',
          value: this.activityGroupId,
        });
      }, 600);
    }
    if (this.showDrawer) {
      this.showDrawerModal();
    }
  }

  onConfirmCancelBookings() {
    this.showCancellingSpinner = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('booking');
    this.$bpcApi
      .post(basePath + '/cancel', {
        bookings: [this.entityId],
        cancelReason: this.$refs.stdCancelForm.formModel['cancel_reason'],
        cancelReasonDescription: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        cancelOrders: this.$refs.stdCancelForm.formModel['cancel_orders'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.showCancellingSpinner = false;
          this.onCloseCancelModal();
          this.onFormClose();
          this.$emit('on-booking-canceled');
        },
        error => {
          this.showCancellingSpinner = false;
          this.cancelling = false;
          if (error?.response?.data?.errors) {
            this.$bpcMessage.showErrorMessage({ text: error?.response?.data?.message });
          } else {
            this.$bpcMessage.showErrorMessage({ text: 'global.generic.server_error' });
          }
        },
      );
  }

  onOpenPaymentModal(entity) {
    this.showPaymentModal = true;
  }

  onCloseAddpaymentModal() {
    this.showPaymentModal = false;
  }

  onPaymentCreated() {
    this.showPaymentModal = false;
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.$emit('payment-created');
  }

  showDrawerModal() {
    this.showForm = false;
    this.$store.commit(`${this.storeName}/setIsDirty`, false);
    this.$bpcStorage.clear('booking-events');
    if (this.entityId) {
      this.$store.commit(`${this.storeName}/setCurrentAction`, 'edit'); // To indicate that we are not in the search of booking
    }
    this.showForm = true;
    this.showDrawerSection = true;
  }

  public checkDeleteBookingAuth() {
    return this.$authorizationService.hasPermission('delete_bookings');
  }

  bookingCanBeConfirmed() {
    return this.bookingStatus === 'draft' || this.bookingStatus === 'requested';
  }
  bookingCanBeRefused() {
    return this.bookingStatus === 'draft' || this.bookingStatus === 'requested';
  }
  bookingCanBeCancelled() {
    return (
      this.bookingStatus === 'draft' ||
      this.bookingStatus === 'requested' ||
      this.bookingStatus === 'confirmed'
    );
  }

  hideDrawerModal() {
    this.$bpcStorage.clear('booking-events');
    this.showForm = false;
    this.showDrawerSection = false;
    this.i++;
  }

  onSavingEntity(e) {
    this.$emit('saving-entity', e);
  }

  onFormSave() {
    this.$emit('next-action', '');
    this.$refs.bookingForm.clickOnSaveButton();
  }

  onFormSaveAndPay() {
    this.$emit('next-action', 'pay');
    this.$refs.bookingForm.clickOnSaveButton();
  }

  onFormSaved(e) {
    this.$emit('entity-saved', e);
    this.i++;
  }

  onFormSearch() {
    this.$refs.bookingForm.clickOnSearchButton();
  }

  onFormReplace() {
    this.$emit('next-action', '');
    this.$refs.bookingForm.clickOnReplaceButton();
  }

  onFormReplaceAndPay() {
    this.$emit('next-action', 'pay');
    this.$refs.bookingForm.clickOnReplaceButton();
  }

  onFormClose() {
    this.hideDrawerModal();
    this.$emit('form-closed');
    this.deleting = false;
  }

  onOpenDeleteModal() {
    this.$bvModal.show('single-booking-delete-modal');
  }

  onCloseDeleteModal() {
    this.$bvModal.hide('single-booking-delete-modal');
  }

  onBookingDelete() {
    this.deleting = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('booking');
    this.$bpcApi
      .post(basePath + '/' + this.entityId, {
        entityIdOrIds: this.entityId,
        _method: 'DELETE',
      })
      .subscribe(
        res => {
          this.onCloseDeleteModal();
          this.onFormClose();
          this.$emit('on-booking-soft-deleted');
        },
        error => {
          this.deleting = false;
          this.$bpcMessage.showErrorMessage({ text: 'global.booking.delete.error' });
        },
      );
  }

  onOpenCancelModal() {
    this.$bvModal.show('single-booking-cancel-modal');
  }

  onCloseCancelModal() {
    this.$bvModal.hide('single-booking-cancel-modal');
  }

  onOpenRefuseModal() {
    this.$bvModal.show('single-booking-refuse-modal');
  }

  onCloseRefuseModal() {
    this.$bvModal.hide('single-booking-refuse-modal');
  }

  onBookingRefuse() {
    this.refusing = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('booking');
    this.$bpcApi.post(basePath + '/refuse/' + this.entityId).subscribe(
      res => {
        this.onCloseRefuseModal();
        this.onFormClose();
        this.$emit('on-booking-refused');
      },
      error => {
        this.refusing = false;
        this.$bpcMessage.showErrorMessage({ text: 'global.booking.refuse.error' });
      },
    );
  }

  onOpenConfirmModal() {
    this.$bvModal.show('single-booking-confirm-modal');
  }

  onCloseConfirmModal() {
    this.$bvModal.hide('single-booking-confirm-modal');
  }

  onBookingConfirm() {
    this.confirming = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('booking');
    this.$bpcApi.post(basePath + '/confirm/' + this.entityId).subscribe(
      res => {
        this.onCloseConfirmModal();
        this.onFormClose();
        this.$emit('on-booking-confirmed');
      },
      error => {
        this.confirming = false;
        this.$bpcMessage.showErrorMessage({ text: 'global.booking.confirm.error' });
      },
    );
  }

  onCloseModal() {
    this.$bvModal.hide('modal-cancel-bookings');
  }

  @Watch('showDrawer')
  onShowChange() {
    if (this.showDrawer) {
      this.showDrawerModal();
    } else {
      this.hideDrawerModal();
    }
  }

  public async destroyed() {
    // await this.$store.dispatch(`${this.storeName}/resetForm`);
  }

  onFormEdit() {
    this.$emit('on-form-edit');
  }

  get openPosSessionId(): PosSession {
    return this.$store.getters['pos/openPosSessionId'];
  }

  // updated () { }
  // destroyed () { }
}
